import React, { useState, useEffect, useCallback } from 'react';
import {
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Tabs,
  Tab,
  Alert,
  Switch,
  FormControlLabel,
  TextField,
  DialogTitle,
  DialogContentText,
} from '@mui/material';
import AppDialog, { AppDialogHeader } from '../common/AppDialog';
import {
  Inventory as InventoryIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Tune as TuneIcon,
  History as HistoryIcon,
  ViewList as ViewListIcon,
} from '@mui/icons-material';
import { Item, InventoryBatch, InventoryTransaction, Role } from '../../types';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { apiClient } from '../../services/api';
import { ApiError } from '../../services/api/http';
import { formatCurrency } from '../../utils/currency';
import { errorMessage } from '../../utils/errorMessage';
import { tracksBatches, tracksInventory } from '../../utils/inventoryMode';
import AddStockDialog from './AddStockDialog';
import RemoveStockDialog from './RemoveStockDialog';
import AdjustStockDialog from './AdjustStockDialog';
import BatchListView from './BatchListView';
import TransactionHistoryView from './TransactionHistoryView';

interface InventoryManagementDialogProps {
  open: boolean;
  onClose: () => void;
  item: Item | null;
  onItemUpdated: (item: Item) => void;
  batchTrackingEnabled?: boolean;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`inventory-tabpanel-${index}`}
      aria-labelledby={`inventory-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );
}

const InventoryManagementDialog: React.FC<InventoryManagementDialogProps> = ({
  open,
  onClose,
  item,
  onItemUpdated,
  batchTrackingEnabled,
}) => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [batches, setBatches] = useState<InventoryBatch[]>([]);
  const [transactions, setTransactions] = useState<InventoryTransaction[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentItem, setCurrentItem] = useState<Item | null>(item);
  
  // Dialog states
  const [addStockOpen, setAddStockOpen] = useState(false);
  const [removeStockOpen, setRemoveStockOpen] = useState(false);
  const [adjustStockOpen, setAdjustStockOpen] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState<InventoryBatch | null>(null);
  const [batchNumberForTracking, setBatchNumberForTracking] = useState('');
  const [pendingBatchTrackingChange, setPendingBatchTrackingChange] = useState<boolean | null>(null);

  const canWrite = user?.role === Role.COMPANY_ADMIN || user?.role === Role.EMPLOYEE;
  const canManageSettings = user?.role === Role.COMPANY_ADMIN;
  const canUseBatchTracking = batchTrackingEnabled !== false;

  const loadInventoryData = useCallback(async (itemToLoad: Item | null = currentItem) => {
    if (!itemToLoad) return;
    setLoading(true);
    setError(null);
    try {
      if (canUseBatchTracking && tracksBatches(itemToLoad)) {
        const batchesData = await apiClient.getInventoryBatches(itemToLoad.id);
        setBatches(batchesData);
      } else {
        setBatches([]);
      }
      const transactionsData = await apiClient.getInventoryTransactions(itemToLoad.id);
      setTransactions(transactionsData);
    } catch (err: unknown) {
      setError(errorMessage(err, 'Failed to load inventory data'));
    } finally {
      setLoading(false);
    }
  }, [canUseBatchTracking, currentItem]);

  useEffect(() => {
    setCurrentItem(item);
    setTabValue(0);
    setSelectedBatch(null);
  }, [item]);

  useEffect(() => {
    if (open && currentItem) {
      loadInventoryData();
    }
  }, [open, currentItem, loadInventoryData]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const requestBatchTrackingToggle = () => {
    if (!currentItem || !canManageSettings) return;
    setError(null);
    setPendingBatchTrackingChange(!tracksBatches(currentItem));
  };

  const handleBatchTrackingToggle = async () => {
    if (!currentItem || !canManageSettings) return;

    const enabling = pendingBatchTrackingChange ?? !tracksBatches(currentItem);
    setPendingBatchTrackingChange(null);
    const normalizedBatchNumber = batchNumberForTracking.trim();

    try {
      const response = await apiClient.toggleBatchTracking(
        currentItem.id,
        enabling,
        normalizedBatchNumber || undefined,
      );
      if (response.success) {
        // Fetch the updated item from backend to ensure sync
        const updatedItem = await apiClient.getItem(currentItem.id);
        setCurrentItem(updatedItem);
        onItemUpdated(updatedItem);
        setBatchNumberForTracking('');
        await loadInventoryData(updatedItem);
      } else {
        const messageByCode: Record<string, string> = {
          ITEM_NOT_FOUND: t('inventory.itemNotFound'),
          INVENTORY_NOT_TRACKED: t('inventory.inventoryNotTracked'),
          INVENTORY_MODE_UNCHANGED: t('inventory.inventoryModeUnchanged'),
          INVENTORY_TRANSITION_CONFLICT: t('inventory.batchNumberAlreadyExists'),
          INVENTORY_TRANSITION_NOT_ALLOWED: t('inventory.inventoryTransitionNotAllowed'),
          INVENTORY_TRANSITION_FAILED: t('inventory.inventoryTransitionFailed'),
        };
        setError((response.code && messageByCode[response.code]) || response.message || t('inventory.batchTrackingToggleError'));
      }
    } catch (err: unknown) {
      const errorCode = err instanceof ApiError ? err.code : undefined;
      const messageByCode: Record<string, string> = {
        ITEM_NOT_FOUND: t('inventory.itemNotFound'),
        INVENTORY_TRANSITION_CONFLICT: t('inventory.batchNumberAlreadyExists'),
        INVENTORY_TRANSITION_NOT_ALLOWED: t('inventory.inventoryTransitionNotAllowed'),
        INVENTORY_TRANSITION_FAILED: t('inventory.inventoryTransitionFailed'),
      };
      setError((errorCode && messageByCode[errorCode]) || errorMessage(err, t('inventory.batchTrackingToggleError')));
    }
  };

  const handleStockOperationComplete = async () => {
    // Refresh item data and inventory data
    setLoading(true);
    try {
      const updatedItem = await apiClient.getItem(currentItem!.id);
      setCurrentItem(updatedItem);
      onItemUpdated(updatedItem);
      await loadInventoryData(updatedItem);
    } catch (err: unknown) {
      setError(errorMessage(err, 'Failed to refresh item data'));
    } finally {
      setLoading(false);
    }
  };

  const handleBatchSelect = (batch: InventoryBatch) => {
    if (canWrite) {
      setSelectedBatch(batch);
      setAdjustStockOpen(true);
    }
  };

  const handleAdjustDialogClose = () => {
    setAdjustStockOpen(false);
    setSelectedBatch(null);
  };

  if (!currentItem) return null;

  const isNonStock = !tracksInventory(currentItem);
  const isOutOfStock = !isNonStock && currentItem.totalStockQuantity <= 0;
  const isLowStock = !isNonStock && currentItem.totalStockQuantity <= 10 && currentItem.totalStockQuantity > 0;

  return (
    <>
      <AppDialog presentation="workflow" open={open} onClose={onClose} maxWidth="lg" fullWidth PaperProps={{ sx: { borderRadius: { xs: 0, sm: 2 }, maxHeight: { xs: '100%', sm: '92vh' } } }}>
        <AppDialogHeader icon={<InventoryIcon />} subtitle={currentItem.name}>{t('inventory.manageInventory')}</AppDialogHeader>

        <DialogContent sx={{ p: { xs: 2, sm: 3 }, pt: { xs: 3, sm: 4 } }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' }, mb: 2 }}>
            {currentItem.name}
          </Typography>
          {error && (
            <Alert severity="error" sx={{ mt: 1, mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Stock Summary */}
          <Typography variant="h6" sx={{ mb: 1.5 }}>{t('inventory.stockOverview')}</Typography>
          <Box sx={{ mb: 2, display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(3, 1fr)' }, gap: 1.5 }}>
            <Box sx={{ p: 2, border: '1px solid', borderColor: isOutOfStock ? 'error.light' : isLowStock ? 'warning.light' : 'success.light', borderRadius: 1.5, bgcolor: 'action.hover' }}>
              <Typography variant="body2" color="text.secondary">{t('inventory.currentStock')}</Typography>
              <Typography variant="h4" sx={{ color: isNonStock ? 'text.secondary' : isOutOfStock ? 'error.main' : isLowStock ? 'warning.main' : 'success.main', fontWeight: 750 }}>{isNonStock ? t('items.inventoryNotTracked') : <>{currentItem.totalStockQuantity}<Typography component="span" variant="body2" sx={{ ml: .5 }}>{t('inventory.units')}</Typography></>}</Typography>
            </Box>
            <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}><Typography variant="body2" color="text.secondary">{t('inventory.price')}</Typography><Typography variant="h6" sx={{ mt: .5 }}>{formatCurrency(currentItem.priceGross ?? currentItem.price)}</Typography></Box>
            <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1.5, gridColumn: { xs: '1 / -1', md: 'auto' } }}><Typography variant="body2" color="text.secondary">{t('inventory.batches')}</Typography><Typography variant="h6" sx={{ mt: .5 }}>{tracksBatches(currentItem) ? batches.length : '—'}</Typography></Box>
          </Box>

          {canManageSettings && canUseBatchTracking && !isNonStock && <Box sx={{ mb: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
            <Typography variant="subtitle1" fontWeight={700}>{t('inventory.batchSettings')}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{t('inventory.batchTrackingHelp')}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={tracksBatches(currentItem)}
                        onChange={requestBatchTrackingToggle}
                        size="small"
                      />
                    }
                    label={t('inventory.batchTracking')}
                  />
                  {!tracksBatches(currentItem) && currentItem.totalStockQuantity > 0 && (
                    <TextField
                      size="small"
                      label={t('inventory.batchNumber')}
                      value={batchNumberForTracking}
                      onChange={(event) => setBatchNumberForTracking(event.target.value)}
                      helperText={t('inventory.batchNumberOptionalForExistingStock')}
                    />
                  )}
                </Box>
          </Box>}

          {/* Quick Actions */}
          {canWrite && !isNonStock && (
            <Box sx={{ mb: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1.5, bgcolor: 'action.hover' }}>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                {t('inventory.quickActions')}
              </Typography>
              <Box display="flex" gap={1} flexWrap="wrap">
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setAddStockOpen(true)}
                >
                  {t('inventory.addStock')}
                </Button>
                <Button variant="outlined" startIcon={<RemoveIcon />} onClick={() => setRemoveStockOpen(true)} disabled={isOutOfStock} sx={{ bgcolor: 'background.paper' }}>{t('inventory.removeStock')}</Button>
                <Button variant="outlined" startIcon={<TuneIcon />} onClick={() => { setSelectedBatch(null); setAdjustStockOpen(true); }} sx={{ bgcolor: 'background.paper' }}>{t('inventory.adjustStock')}</Button>
              </Box>
              {isOutOfStock && <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>{t('inventory.noStockToRemove')}</Typography>}
            </Box>
          )}

          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tabValue} onChange={handleTabChange}>
              {canUseBatchTracking && !isNonStock && tracksBatches(currentItem) && (
                <Tab
                  icon={<ViewListIcon />}
                  label={t('inventory.batches')}
                  iconPosition="start"
                />
              )}
              <Tab
                icon={<HistoryIcon />}
                label={t('inventory.transactions')}
                iconPosition="start"
              />
            </Tabs>
          </Box>

          {/* Tab Panels */}
          {canUseBatchTracking && !isNonStock && tracksBatches(currentItem) && (
            <TabPanel value={tabValue} index={0}>
              <BatchListView
                batches={batches}
                loading={loading}
                onRefresh={loadInventoryData}
                onBatchSelect={handleBatchSelect}
              />
            </TabPanel>
          )}

          <TabPanel value={tabValue} index={canUseBatchTracking && !isNonStock && tracksBatches(currentItem) ? 1 : 0}>
            <TransactionHistoryView
              transactions={transactions}
              loading={loading}
              onRefresh={loadInventoryData}
              onBatchClick={(batchId) => {
                const batch = batches.find(b => b.id === batchId);
                if (batch && canWrite) {
                  setSelectedBatch(batch);
                  setAdjustStockOpen(true);
                }
              }}
            />
          </TabPanel>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>{t('common.close')}</Button>
        </DialogActions>
      </AppDialog>

      <AppDialog
        presentation="confirm"
        open={pendingBatchTrackingChange !== null}
        onClose={() => setPendingBatchTrackingChange(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {pendingBatchTrackingChange
            ? t('inventory.enableBatchTrackingConfirmTitle')
            : t('inventory.disableBatchTrackingConfirmTitle')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {pendingBatchTrackingChange
              ? t('inventory.enableBatchTrackingConfirmMessage')
              : t('inventory.disableBatchTrackingConfirmMessage')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingBatchTrackingChange(null)}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={handleBatchTrackingToggle}>
            {t('common.confirm')}
          </Button>
        </DialogActions>
      </AppDialog>

      {/* Stock Operation Dialogs */}
      <AddStockDialog
        open={addStockOpen}
        onClose={() => setAddStockOpen(false)}
        item={currentItem}
        onSuccess={handleStockOperationComplete}
        batchTrackingEnabled={batchTrackingEnabled}
      />

      <RemoveStockDialog
        open={removeStockOpen}
        onClose={() => setRemoveStockOpen(false)}
        item={currentItem}
        onSuccess={handleStockOperationComplete}
        batchTrackingEnabled={batchTrackingEnabled}
      />

      <AdjustStockDialog
        open={adjustStockOpen}
        onClose={handleAdjustDialogClose}
        item={currentItem}
        batch={selectedBatch}
        onSuccess={handleStockOperationComplete}
        batchTrackingEnabled={batchTrackingEnabled}
      />
    </>
  );
};

export default InventoryManagementDialog;
